"use client"

import { useState, CSSProperties, useEffect, useRef } from "react"

/**
 * MDC Animation Utilities
 * Following Motion Design Cursor Principles for React web applications
 */

// Check for reduced motion preference
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Custom hook for smooth hover animations following MDC principles
 * MICRO-FEEDBACK: Instant feedback (100-150ms) with ease-out for entry
 * PURPOSE-DRIVEN: Inform user of interactivity
 * ACCESSIBILITY: Respects user motion preferences
 */
export function useHoverAnimation(scale = 1.05, duration = 150) {
  const [isHovered, setIsHovered] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
  }, [])

  const hoverProps: {
    onMouseEnter: () => void
    onMouseLeave: () => void
    className: string
    style: CSSProperties
  } = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    className: reducedMotion 
      ? "transition-opacity duration-150"
      : `transition-all duration-${duration}`,
    style: reducedMotion
      ? ({
          opacity: isHovered ? 0.8 : 1,
          transition: "opacity 150ms ease-out",
        } as CSSProperties)
      : ({
          transform: isHovered ? `scale(${scale})` : "scale(1)",
          transition: `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          willChange: "transform",
        } as CSSProperties),
  }

  return { isHovered, hoverProps }
}

/**
 * Hook for card hover effects with elevated shadow
 * MICRO-FEEDBACK: Instant visual elevation (150ms)
 * MOTION CUE: Pre-interaction hint with shadow intensification
 * HIERARCHY: Scales with UI importance
 * NATURAL-PHYSICS: Uses cubic-bezier for natural motion
 */
export function useCardHover(scale = 1.02, shadowIntensity: "sm" | "md" | "lg" = "md") {
  const [isHovered, setIsHovered] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
  }, [])

  const shadowMap = {
    sm: {
      default: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      hover: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    },
    md: {
      default: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      hover: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    lg: {
      default: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      hover: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },
  }

  const shadows = shadowMap[shadowIntensity]

  const cardProps: {
    onMouseEnter: () => void
    onMouseLeave: () => void
    style: CSSProperties
  } = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    style: reducedMotion
      ? ({
          opacity: isHovered ? 0.9 : 1,
          transition: "opacity 150ms ease-out",
        } as CSSProperties)
      : ({
          transform: isHovered ? `translateY(-8px) scale(${scale})` : "translateY(0) scale(1)",
          transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isHovered ? shadows.hover : shadows.default,
          willChange: "transform, box-shadow",
        } as CSSProperties),
  }

  return { isHovered, cardProps }
}

/**
 * Hook for button press animations
 * MICRO-FEEDBACK: Instant response to tap/click
 * CONTINUITY: Natural spring back on release
 */
export function useButtonPress(activeScale = 0.95, duration = 120) {
  const [isPressed, setIsPressed] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
  }, [])

  const pressProps: {
    onMouseDown: () => void
    onMouseUp: () => void
    onMouseLeave: () => void
    style: CSSProperties
  } = {
    onMouseDown: () => setIsPressed(true),
    onMouseUp: () => setIsPressed(false),
    onMouseLeave: () => setIsPressed(false),
    style: reducedMotion
      ? ({} as CSSProperties)
      : ({
          transform: isPressed ? `scale(${activeScale})` : "scale(1)",
          transition: `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        } as CSSProperties),
  }

  return { isPressed, pressProps }
}

/**
 * Hook for stagger animations - delays children's entry
 * CONTINUITY: Creates flowing, sequential appearance
 * HIERARCHY: Primary items appear first
 */
export function useStagger(delay = 100) {
  const [reducedMotion] = useState(false) // Would use actual check

  return {
    transition: reducedMotion
      ? "opacity 150ms ease-out"
      : "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    transitionDelay: reducedMotion ? "0ms" : `${delay}ms`,
  }
}

/**
 * Hook for entry animations
 * MOTION CUE: Draws attention to new content
 * NATURAL-PHYSICS: Uses natural easing curves
 */
export function useEntryAnimation(direction: "up" | "down" | "left" | "right" = "up") {
  const [visible, setVisible] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
    setVisible(true)
  }, [])

  const directionMap = {
    up: { from: "translateY(20px)", to: "translateY(0)" },
    down: { from: "translateY(-20px)", to: "translateY(0)" },
    left: { from: "translateX(20px)", to: "translateX(0)" },
    right: { from: "translateX(-20px)", to: "translateX(0)" },
  }

  const directions = directionMap[direction]

  return {
    style: reducedMotion
      ? ({
          opacity: visible ? 1 : 0,
          transition: "opacity 200ms ease-out",
        } as CSSProperties)
      : ({
          opacity: visible ? 1 : 0,
          transform: visible ? directions.to : directions.from,
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        } as CSSProperties),
  }
}

/**
 * Hook for scroll-triggered animations using IntersectionObserver
 * MOTION CUE: Animates elements when they enter viewport
 * NATURAL-PHYSICS: Uses natural easing curves
 * ACCESSIBILITY: Respects reduced motion preference
 * PURPOSE-DRIVEN: Informs user of content appearance
 */
export function useScrollAnimation(
  direction: "up" | "down" | "left" | "right" | "fade" = "up",
  threshold: number = 0.1,
  rootMargin: string = "0px"
) {
  const [isVisible, setIsVisible] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const elementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
  }, [])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => {
      if (element) {
        observer.unobserve(element)
      }
    }
  }, [threshold, rootMargin])

  const directionMap = {
    up: { from: "translateY(30px)", to: "translateY(0)" },
    down: { from: "translateY(-30px)", to: "translateY(0)" },
    left: { from: "translateX(30px)", to: "translateX(0)" },
    right: { from: "translateX(-30px)", to: "translateX(0)" },
    fade: { from: "translateY(0)", to: "translateY(0)" },
  }

  const directions = directionMap[direction]

  const animationStyle = reducedMotion
    ? ({
        opacity: isVisible ? 1 : 0,
        transition: "opacity 300ms ease-out",
      } as CSSProperties)
    : ({
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? directions.to : directions.from,
        transition: "opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
      } as CSSProperties)

  return {
    ref: elementRef,
    isVisible,
    style: animationStyle,
  }
}

/**
 * Hook for toggle animations (expand/collapse)
 * CONTINUITY: Smooth state transitions
 * MICRO-FEEDBACK: Visual confirmation of state change
 */
export function useToggleAnimation(isOpen: boolean, duration = 200) {
  const [reducedMotion] = useState(prefersReducedMotion())

  return {
    maxHeight: isOpen ? "9999px" : "0",
    overflow: "hidden",
    transition: reducedMotion
      ? `max-height ${duration}ms ease-out`
      : `max-height ${duration}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    opacity: isOpen ? 1 : 0,
  }
}

/**
 * Hook for loading skeleton animations
 * MOTION CUE: Indicates loading state
 * ACCESSIBILITY: Respects reduced motion
 */
export function useSkeletonAnimation() {
  const [reducedMotion] = useState(prefersReducedMotion())

  return {
    animation: reducedMotion
      ? "none"
      : "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  }
}

/**
 * Hook for progress bar animations
 * MICRO-FEEDBACK: Shows progress in real-time
 * CONTINUITY: Smooth updates
 */
export function useProgressAnimation(duration = 400) {
  const [reducedMotion] = useState(prefersReducedMotion())

  return {
    transition: reducedMotion
      ? "width 200ms linear"
      : `width ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  }
}

/**
 * Hook for pulse/glow animation
 * MOTION CUE: Draws attention subtly
 * HIERARCHY: Used for important CTAs
 * ACCESSIBILITY: Disabled for reduced motion
 */
export function usePulse() {
  const [reducedMotion] = useState(prefersReducedMotion())

  return {
    animation: reducedMotion
      ? "none"
      : "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  }
}

/**
 * Utility class for MDC animations
 * Combine multiple effects for complex interactions
 */
export class MDCAnimation {
  static cardHover = "transition-all duration-250 ease-out hover:shadow-lg"
  static buttonHover = "transition-all duration-150 ease-out active:scale-95"
  static tabTransition = "transition-all duration-200 ease-in-out"
  static modalOverlay = "transition-opacity duration-200 ease-out"
  
  // Motion-safe versions
  static safe(className: string) {
    return className + " motion-safe:" + className.split(" ").join(" motion-safe:")
  }
}

